<div class="repeating-event-wrapper" *ngIf="guild">
  <!-- Enhanced Header Section -->
  <div class="template-header">
    <div class="header-content">
      <h1 class="template-title">Repeating Events</h1>
      <p class="template-description">
        <code>Repeating Events</code> are entries that trigger an event creation at a specific interval.
        <a href="https://docs.raidorganizer.org/repeating-events/" target="_blank" class="docs-link">Read more about them here</a>.
      </p>
    </div>
  </div>

  <!-- Main Content Grid -->
  <div class="row g-4">
    <!-- Settings Column -->
    <div class="col-12 col-xxl settings-column">
      <div class="section-header">
        <h2 class="section-title">Event Setup</h2>
        <mat-icon class="section-icon">event</mat-icon>
      </div>
    <!-- Select channel and event template -->
    <form [formGroup]="eventForm" (ngSubmit)="onSubmit()">
      <div class="row pe-3">
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label>Event/LFG Channel </mat-label>
            <mat-select id="eventChannel" name="eventChannel" formControlName="eventChannel">
              <ng-container *ngFor="let channel of allowedChannels">
                <mat-option [value]="channel.id">
                  {{ channel.name }}
                </mat-option>
              </ng-container>
            </mat-select>
          </mat-form-field>
        </div>
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label>Signup Group (Premium)</mat-label>
            <mat-select formControlName="signupGroup" [disabled]="!guild?.has_premium">
              <mat-option *ngFor="let group of filteredSignupGroups" [value]="group.identifier">
                {{ group.signupGroupTitle }}
              </mat-option>
              <mat-option *ngIf="filteredSignupGroups.length === 0" disabled>
                No signup group for the selected signup template
              </mat-option>
            </mat-select>
            <mat-hint *ngIf="!guild?.has_premium">Requires Premium
            </mat-hint>
          </mat-form-field>
        </div>
      </div>
      <div class="section-header mt-4">
        <h2 class="section-title">Event Details</h2>
        <mat-icon class="section-icon">edit</mat-icon>
      </div>
      <!-- Title -->
      <mat-form-field class="full-width-container">
        <mat-label>Title </mat-label>
        <input matInput formControlName="title">
        <mat-error *ngIf="eventForm.get('title').hasError('required')">
          Title is required
        </mat-error>
        <mat-error *ngIf="eventForm.get('title').errors?.maxlength">Title is too long (max.
          60)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('title').value.length}} / 60</mat-hint>
      </mat-form-field>

      <div class="row pe-3">
        <!-- Start Date and Time -->
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label *ngIf="!eventForm.get('start').disabled">Start Date and Time </mat-label>
            <mat-label *ngIf="eventForm.get('start').disabled">Start Date and Time <span role="img" aria-label="danger"
                matTooltip="Can not be edited." matTooltipPosition="above"
                style="cursor: help;">
                ⚠️
              </span>
            </mat-label>
            <input matInput [ngxMatDatetimePicker]="startPicker" placeholder="Choose a date" formControlName="start"
              [min]="startMinDate" [max]="maxDate">
            <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
            <ngx-mat-datetime-picker #startPicker [touchUi]="touchUi" [enableMeridian]="enableMeridian"
              [showSeconds]="false">
            </ngx-mat-datetime-picker>
            <mat-error *ngIf="eventForm.get('start').hasError('required')">
              Start Date and Time is required
            </mat-error>
          </mat-form-field>
        </div>
        <!-- End Date and Time -->
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label *ngIf="!eventForm.get('end').disabled">End Date and Time </mat-label>
            <mat-label *ngIf="eventForm.get('end').disabled">End Date and Time <span role="img" aria-label="danger"
                matTooltip="Can not be edited." matTooltipPosition="above"
                style="cursor: help;">
                ⚠️
              </span>
            </mat-label>
            <input matInput [ngxMatDatetimePicker]="endPicker" placeholder="Choose a date" formControlName="end"
              [min]="endMinDateIso" [max]="maxDate">
            <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
            <ngx-mat-datetime-picker #endPicker [touchUi]="touchUi" [enableMeridian]="enableMeridian">
            </ngx-mat-datetime-picker>
            <mat-error *ngIf="eventForm.get('end').hasError('matDatetimePickerMin')">
              End Date / Time needs to be after Start Date / Time
            </mat-error>
          </mat-form-field>
        </div>
      </div>

      <!-- Description -->
      <mat-form-field class="full-width-container">
        <mat-label>Description</mat-label>
        <textarea matInput formControlName="description"></textarea>
        <mat-error *ngIf="eventForm.get('description').errors?.maxlength">Description is too long (max.
          700)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('description').value.length}} / 700</mat-hint>
      </mat-form-field>

      <!-- Signup Template -->
      <mat-form-field class="full-width-container">
        <mat-label>Signup Template </mat-label>
        <mat-select id="signupTemplate" formControlName="signupTemplate">
          <ng-container *ngFor="let tempalte of signupTemplates">
            <mat-option [value]="tempalte.identifier">
              {{ tempalte.name }}
            </mat-option>
          </ng-container>
        </mat-select>
      </mat-form-field>

      <!-- Text -->
      <mat-form-field class="full-width-container">
        <mat-label>Text</mat-label>
        <textarea matInput formControlName="text"></textarea>
        <mat-error *ngIf="eventForm.get('text').errors?.maxlength">Footer is too long (max.
          2000)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('text').value.length}} / 2000</mat-hint>
      </mat-form-field>

      <!-- Footer Text -->
      <mat-form-field class="full-width-container">
        <mat-label>Footer Text (Premium)</mat-label>
        <textarea matInput formControlName="footerText"></textarea>
        <mat-error *ngIf="eventForm.get('footerText').errors?.maxlength">Footer is too long (max.
          1900)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('footerText').value.length}} / 1900</mat-hint>
      </mat-form-field>

      <!-- Footer Icon -->
      <mat-form-field class="full-width-container">
        <mat-label>Footer Icon (Premium)</mat-label>
        <input matInput formControlName="footerIcon">
      </mat-form-field>

      <!-- Embed Image -->
      <mat-form-field class="full-width-container">
        <mat-label>Embed Image URL (Premium)</mat-label>
        <input matInput formControlName="embedImage">
      </mat-form-field>

      <!-- Embed Thumbnail -->
      <mat-form-field class="full-width-container">
        <mat-label>Embed Thumbnail URL (Premium)</mat-label>
        <input matInput formControlName="embedThumbnail">
      </mat-form-field>

      <div class="section-header mt-4">
        <h2 class="section-title">Repeat Config</h2>
        <mat-icon class="section-icon">repeat</mat-icon>
      </div>
      <div class="row pe-3">
        <!-- Days In Advance -->
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container" appearance="fill">
            <mat-label *ngIf="!eventForm.get('daysInAdvance').disabled">Days In Advance</mat-label>
            <mat-label *ngIf="eventForm.get('daysInAdvance').disabled">Days In Advance<span role="img" aria-label="danger"
                matTooltip="Can not be edited." matTooltipPosition="above"
                style="cursor: help;">
                ⚠️
              </span>
            </mat-label>
            <input matInput type="number" formControlName="daysInAdvance" />
            <mat-error *ngIf="!eventForm.get('daysInAdvance').disabled">0-7 days<span role="img" aria-label="danger"
              matTooltip="Upgrade to Premium to remove the limit." matTooltipPosition="above"
              style="cursor: help;">
              ⚠️
            </span></mat-error>
          </mat-form-field>
        </div>

        <!-- Event Creation Time -->
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field appearance="fill" class="full-width-container">
            <mat-label *ngIf="!eventForm.get('eventCreationTime').disabled">Event Creation Time</mat-label>
            <mat-label *ngIf="eventForm.get('eventCreationTime').disabled">Event Creation Time<span role="img" aria-label="danger"
                matTooltip="Can not be edited." matTooltipPosition="above"
                style="cursor: help;">
                ⚠️
              </span>
            </mat-label>
            <input matInput [ngxMatTimepicker]="picker" formControlName="eventCreationTime"
              [format]="(guild.timeformat === '24h') ? '24' : '12'" [disabled]="!eventForm.enabled">
            <ngx-mat-timepicker #picker></ngx-mat-timepicker>
          </mat-form-field>
        </div>
      </div>

      <!-- Interval Type Selection -->
      <mat-form-field class="full-width-container">
        <mat-label *ngIf="!eventForm.get('intervalType').disabled">Interval Type</mat-label>
        <mat-label *ngIf="eventForm.get('intervalType').disabled">Interval Type<span role="img" aria-label="danger"
            matTooltip="Can not be edited." matTooltipPosition="above"
            style="cursor: help;">
            ⚠️
          </span>
        </mat-label>
        <mat-select formControlName="intervalType">
          <mat-option value="every7Days">Every 7 Days</mat-option>
          <mat-option value="everyXDays">Every X Days</mat-option>
          <mat-option value="dayOfMonth">On the Xth Day of Month</mat-option>
          <mat-option value="nthWeekdayOfMonth">On First/Second/... Monday/Tuesday/... in Month</mat-option>
          <mat-option value="weekly">On Specific Weekdays</mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Additional Controls depending on selection -->

      <!-- Every X Days -->
      <div *ngIf="eventForm.get('intervalType').value === 'everyXDays'">
        <mat-form-field class="full-width-container">
          <mat-label *ngIf="!eventForm.get('intervalDays').disabled">Days Interval</mat-label>
          <mat-label *ngIf="eventForm.get('intervalDays').disabled">Days Interval<span role="img" aria-label="danger"
              matTooltip="Can not be edited." matTooltipPosition="above"
              style="cursor: help;">
              ⚠️
            </span>
          </mat-label>
          <input matInput type="number" min="1" formControlName="intervalDays" placeholder="e.g., 3">
        </mat-form-field>
      </div>

      <!-- Xth Day in Month -->
      <div *ngIf="eventForm.get('intervalType').value === 'dayOfMonth'">
        <mat-form-field class="full-width-container">
          <mat-label *ngIf="!eventForm.get('dayOfMonth').disabled">Day of Month (1-31)</mat-label>
          <mat-label *ngIf="eventForm.get('dayOfMonth').disabled">Day of Month (1-31)<span role="img" aria-label="danger"
              matTooltip="Can not be edited." matTooltipPosition="above"
              style="cursor: help;">
              ⚠️
            </span>
          </mat-label>
          <input matInput type="number" min="1" max="31" formControlName="dayOfMonth" placeholder="e.g., 15">
        </mat-form-field>
      </div>

      <!-- First/Second/... Monday/Tuesday/... in Month -->
      <div *ngIf="eventForm.get('intervalType').value === 'nthWeekdayOfMonth'">
        <div class="row">
          <div class="col-6">
            <mat-form-field class="full-width-container">
              <mat-label *ngIf="!eventForm.get('ordinal').disabled">Ordinal</mat-label>
              <mat-label *ngIf="eventForm.get('ordinal').disabled">Ordinal<span role="img" aria-label="danger"
                  matTooltip="Can not be edited." matTooltipPosition="above"
                  style="cursor: help;">
                  ⚠️
                </span>
              </mat-label>
              <mat-select formControlName="ordinal">
                <mat-option *ngFor="let ord of ['First', 'Second', 'Third', 'Fourth']" [value]="ord">
                  {{ ord }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="col-6">
            <mat-form-field class="full-width-container">
              <mat-label *ngIf="!eventForm.get('weekday').disabled">Weekday</mat-label>
              <mat-label *ngIf="eventForm.get('weekday').disabled">Weekday<span role="img" aria-label="danger"
                  matTooltip="Can not be edited." matTooltipPosition="above"
                  style="cursor: help;">
                  ⚠️
                </span>
              </mat-label>
              <mat-select formControlName="weekday">
                <mat-option
                  *ngFor="let day of ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']"
                  [value]="day">
                  {{ day }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>
      </div>

      <!-- Every Monday/Tuesday/... -->
      <div *ngIf="eventForm.get('intervalType').value === 'weekly'">
        <mat-form-field class="full-width-container">
          <mat-label *ngIf="!eventForm.get('weekdays').disabled">Choose Weekdays</mat-label>
          <mat-label *ngIf="eventForm.get('weekdays').disabled">Choose Weekdays<span role="img" aria-label="danger"
              matTooltip="Can not be edited." matTooltipPosition="above"
              style="cursor: help;">
              ⚠️
            </span>
          </mat-label>
          <mat-select formControlName="weekdays" multiple>
            <mat-option
              *ngFor="let day of ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']"
              [value]="day">
              {{ day }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons-container">
        <div class="primary-actions">
          <button mat-raised-button color="primary" type="submit" [disabled]="!eventForm.valid || creationInProgress"
                  class="save-button">
            <mat-icon>{{ repeatingEventId === null ? 'add' : 'save' }}</mat-icon>
            <span>{{ repeatingEventId === null ? 'Create' : 'Update' }} Repeating Event</span>
          </button>
          <div *ngIf="creationInProgress" class="loading-indicator">
            <mat-spinner diameter="20"></mat-spinner>
            <span>Creating repeating event...</span>
          </div>
        </div>
        <div class="secondary-actions" *ngIf="repeatingEventId !== null">
          <button mat-stroked-button color="warn" type="button" (click)="deleteRepeatingEntry()" class="delete-button">
            <fa-icon [icon]="faIcons.trash"></fa-icon>
            <span> Delete Repeating Entry</span>
          </button>
        </div>
      </div>
    </form>
    </div>

    <!-- Divider -->
    <div class="col-auto d-none d-xxl-flex align-items-stretch px-0">
      <div class="content-divider"></div>
    </div>

    <!-- Preview Column -->
    <div class="col-12 col-xxl-auto preview-column">
      <div class="section-header">
        <h2 class="section-title">Preview</h2>
        <mat-icon class="section-icon">visibility</mat-icon>
      </div>
      <div class="preview-container">
        <app-embed-preview></app-embed-preview>
      </div>
    </div>
  </div>
</div>

<div *ngIf="!guild">
  <p>Loading...</p>
</div>
